"use client"

import { usePagination } from "@mantine/hooks"
import { getPageLink } from "@primoui/utils"
import { usePathname, useSearchParams } from "next/navigation"
import { useTranslations } from "next-intl"
import type { ComponentProps } from "react"
import { Note } from "~/components/legacy/common/note"
import { ArrowLeftIcon, ArrowRightIcon } from "~/components/ui/icons"
import { NavLink, navLinkVariants } from "~/components/legacy/web/ui/nav-link"
import { cx } from "~/lib/legacy-utils"

type PaginationLinkProps = ComponentProps<typeof NavLink> & {
  isDisabled?: boolean
}

type PaginationRangeItem = {
  value: number | "dots"
  key: string
}

const PaginationLink = ({
  isDisabled,
  children,
  prefix,
  suffix,
  ...props
}: PaginationLinkProps) => {
  if (isDisabled) {
    return (
      <NavLink prefix={prefix} suffix={suffix} asChild>
        <button type="button" disabled>
          {children}
        </button>
      </NavLink>
    )
  }

  return (
    <NavLink prefix={prefix} suffix={suffix} {...props}>
      {children}
    </NavLink>
  )
}

export type PaginationProps = ComponentProps<"nav"> & {
  total: number
  perPage?: number
  page?: number
  siblings?: number
  boundaries?: number
  basePath?: string
  /** Anchor id to scroll to on page change. Defaults to "tools". */
  scrollToId?: string
}

export const Pagination = ({
  className,
  total,
  perPage = 1,
  page = 1,
  siblings,
  boundaries,
  basePath,
  scrollToId = "tools",
  ...props
}: PaginationProps) => {
  const t = useTranslations("components.pagination")
  const currentPathname = usePathname()
  const pathname = basePath ?? currentPathname
  const searchParams = useSearchParams()
  const params = new URLSearchParams(searchParams)
  const pageCount = Math.ceil(total / perPage)

  const pagination = usePagination({
    total: pageCount,
    page,
    siblings,
    boundaries,
  })

  const pageLink = (p: number) => `${getPageLink(params, pathname, p)}#${scrollToId}`

  if (pagination.range.length <= 1) {
    return null
  }

  const paginationRangeItems = pagination.range.reduce<{
    items: PaginationRangeItem[]
    lastPage: number | "start"
  }>(
    (acc, value) => {
      if (typeof value === "number") {
        acc.items.push({ value, key: `page-${value}` })
        acc.lastPage = value
        return acc
      }

      acc.items.push({ value, key: `dots-after-${acc.lastPage}` })
      return acc
    },
    { items: [], lastPage: "start" },
  ).items

  return (
    <nav
      className={cx("-mt-px flex w-full items-start justify-between text-sm md:w-auto", className)}
      {...props}
    >
      <PaginationLink
        isDisabled={page <= 1}
        href={pageLink(page - 1)}
        prefix={<ArrowLeftIcon />}
        rel="prev"
      >
        {t("prev")}
      </PaginationLink>

      <Note className="md:hidden">{t("page_of", { page, pageCount })}</Note>

      <div className="flex items-center flex-wrap gap-3 max-md:hidden">
        <Note as="span">{t("page_label")}</Note>

        {paginationRangeItems.map(({ value, key }) => (
          <div key={key}>
            {value === "dots" && <span className={navLinkVariants()}>...</span>}

            {typeof value === "number" && (
              <NavLink
                href={pageLink(value)}
                isActive={value === page}
                className={cx("min-w-5 justify-center", value === page && "bg-accent rounded-xs")}
              >
                {value}
              </NavLink>
            )}
          </div>
        ))}
      </div>

      <PaginationLink
        isDisabled={page >= pageCount}
        href={pageLink(page + 1)}
        suffix={<ArrowRightIcon />}
        rel="next"
      >
        {t("next")}
      </PaginationLink>
    </nav>
  )
}
